import { ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ActionIcon,
    Box,
    Button,
    Card,
    Flex,
    Grid,
    Image,
    LoadingOverlay,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from "@mantine/core";
import { IconInfoCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import { pick } from "lodash";
import { ReviewDestinationInput } from "src/graphql/generated/graphql";
import { useUploadImage } from "src/hooks/files/useUploadImage";
import { handleError } from "src/utils/error";

import { productImageCrop, useQueryProductByEAN } from "../../../hooks/products";
import { SelectImageModal } from "../../../modals/SelectImage";
import { FormItem } from "../../FormItem";
import { ReviewDestinationsEditor } from "../../ReviewDestinationsEditor";
import { RichTextEditor } from "../../RichTextEditor";
import { SelectProductCategory } from "../../SelectProductCategory";
import { useCreateCampaignInquiryFormContext } from "../formContext";

export interface ItemProps {
    index: number;
    reviewDestinations: ReviewDestinationInput[];
    noReviewDestinationsLabel?: ReactNode;
    enableCustomProductReviewDestinations?: boolean;
    onRemove?();
}

export const Item = ({
    index,
    reviewDestinations,
    noReviewDestinationsLabel,
    enableCustomProductReviewDestinations,
    onRemove,
}: ItemProps) => {
    const { t } = useTranslation(["common"]);

    const createCampaignInquiryForm = useCreateCampaignInquiryFormContext();

    const isExistingProduct = !!createCampaignInquiryForm.values?.products?.[index]?.id;

    const [isCheckingEAN, setIsCheckingEAN] = useState(false);
    const { queryProductByEAN } = useQueryProductByEAN();

    const handleCheckEAN = useCallback(
        async (ean: string) => {
            setIsCheckingEAN(true);

            const product = await queryProductByEAN(ean);

            if (product?.id) {
                createCampaignInquiryForm.setFieldValue(`products.${index}`, {
                    key: Math.random().toString(36).substring(2),
                    id: product?.id,
                    ean: product?.codes?.ean,
                    name: product?.name,
                    category: product?.category,
                });
            }

            setIsCheckingEAN(false);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [index]
    );

    useEffect(() => {
        if (
            createCampaignInquiryForm.values?.products?.[index]?.ean?.length === 13 &&
            !createCampaignInquiryForm.values?.products?.[index]?.id &&
            !isCheckingEAN
        ) {
            handleCheckEAN(createCampaignInquiryForm.values?.products?.[index]?.ean);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createCampaignInquiryForm.values?.products?.[index]?.ean]);

    const { uploadImage, isUploadingImage } = useUploadImage();

    const handleImageUpload = useCallback(
        async (file: File) => {
            try {
                const image = await uploadImage(file);

                createCampaignInquiryForm.setFieldValue(`products.${index}.images`, [
                    ...(createCampaignInquiryForm.values?.products?.[index]?.images || []),
                    pick(image, ["id", "objectKey", "src"]),
                ]);

                return image;
            } catch (err) {
                handleError(err, { context: { productId: createCampaignInquiryForm.values?.products?.[index]?.id } });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [JSON.stringify(createCampaignInquiryForm.values?.products?.[index]?.images)]
    );

    const destinations = createCampaignInquiryForm.values?.products?.[index]?.destinations?.map((dest) => {
        return {
            key: dest.key || "",
            name: dest.name || "",
            type: dest.type || "text",
            config: dest.config || "",
            configFields: dest.configFields?.map((field) => ({
                label: field.label || "",
                path: field.path || "",
                type: field.type || "text",
                placeholder: field.placeholder || "",
                isRequiredForApproval: field.isRequiredForApproval || false,
                urlStripQueryString: field.urlStripQueryString || false,
            })),
        };
    });

    return (
        <Card p={36} radius={12}>
            <Card.Section p="md">
                <LoadingOverlay visible={isCheckingEAN || isUploadingImage} />
                <Flex align="stretch">
                    <Grid style={{ flex: 1 }}>
                        <Grid.Col xs={12} md={6}>
                            <TextInput
                                {...createCampaignInquiryForm.getInputProps(`products.${index}.ean`)}
                                label={t("product_code_ean")}
                                placeholder={t("product_code_ean_placeholder")}
                                disabled={isExistingProduct}
                            />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                            <SelectProductCategory
                                {...createCampaignInquiryForm.getInputProps(`products.${index}.category`)}
                                disabled={isExistingProduct}
                                withAsterisk
                            />
                        </Grid.Col>
                        <Grid.Col xs={12}>
                            <TextInput
                                {...createCampaignInquiryForm.getInputProps(`products.${index}.name`)}
                                label={t("product_name")}
                                placeholder={t("product_name")}
                                disabled={isExistingProduct}
                                withAsterisk
                            />
                        </Grid.Col>
                        {!isExistingProduct && (
                            <>
                                <Grid.Col xs={12} md={6}>
                                    <TextInput
                                        {...createCampaignInquiryForm.getInputProps(
                                            `products.${index}.info.manufacturer`
                                        )}
                                        label={t("product_manufacturer")}
                                        placeholder={t("product_manufacturer")}
                                    />
                                </Grid.Col>
                                <Grid.Col xs={12} md={6}>
                                    <TextInput
                                        {...createCampaignInquiryForm.getInputProps(`products.${index}.info.model`)}
                                        label={t("product_model")}
                                        placeholder={t("product_model")}
                                    />
                                </Grid.Col>
                                <Grid.Col xs={12}>
                                    <FormItem
                                        mb="xs"
                                        label={
                                            <Tooltip w="250px" label={t("product_description_info")} multiline>
                                                <Text w="fit-content">
                                                    {t("product_description")} <IconInfoCircle size={12} />
                                                </Text>
                                            </Tooltip>
                                        }
                                    >
                                        <RichTextEditor
                                            placeholder={t("product_description")}
                                            value={createCampaignInquiryForm.values?.products?.[index]?.description}
                                            onChange={(description) =>
                                                createCampaignInquiryForm.setFieldValue(
                                                    `products.${index}.description`,
                                                    description
                                                )
                                            }
                                            onImageUpload={handleImageUpload}
                                        />
                                    </FormItem>
                                </Grid.Col>
                                <Grid.Col xs={12}>
                                    <FormItem mb="xs" label={t("product_thumbnailimage")}>
                                        <Flex>
                                            <Stack spacing="sm">
                                                <Button
                                                    variant="light"
                                                    onClick={() =>
                                                        SelectImageModal.display({
                                                            title: t("thumbnailimage_upload"),
                                                            openFileInputDialogOnMount: true,
                                                            crop: productImageCrop,
                                                            onSelect: (thumbnailImage) => {
                                                                createCampaignInquiryForm.setFieldValue(
                                                                    `products.${index}.thumbnailImage`,
                                                                    pick(thumbnailImage, ["id", "objectKey", "src"])
                                                                );
                                                            },
                                                        })
                                                    }
                                                >
                                                    {t("thumbnailimage_upload")}
                                                </Button>
                                                {!!createCampaignInquiryForm.values?.products?.[index]?.thumbnailImage
                                                    ?.src && (
                                                        <Button
                                                            variant="subtle"
                                                            color="red"
                                                            onClick={() =>
                                                                createCampaignInquiryForm.setFieldValue(
                                                                    `products.${index}.thumbnailImage`,
                                                                    null
                                                                )
                                                            }
                                                        >
                                                            {t("image_remove")}
                                                        </Button>
                                                    )}
                                            </Stack>
                                            <Box ml="md" style={{ flex: 1 }}>
                                                {!!createCampaignInquiryForm.values?.products?.[index]?.thumbnailImage
                                                    ?.src && (
                                                        <Image
                                                            radius="md"
                                                            src={`${createCampaignInquiryForm.values.products[index].thumbnailImage.src}?w=320`}
                                                            width="320px"
                                                        />
                                                    )}
                                            </Box>
                                        </Flex>
                                    </FormItem>
                                </Grid.Col>
                                <Grid.Col xs={12}>
                                    <TextInput
                                        {...createCampaignInquiryForm.getInputProps(`products.${index}.userManualLink`)}
                                        mb="xs"
                                        type="text"
                                        label={t("product_usermanuallink")}
                                        placeholder={t("product_usermanuallink_placeholder")}
                                    />
                                </Grid.Col>
                                <Grid.Col xs={12}>
                                    <FormItem mb="xs" label={t("product_detailpagelinks")}>
                                        <Stack spacing="0">
                                            {(
                                                createCampaignInquiryForm.values?.products?.[index]?.detailPageLinks ||
                                                []
                                            ).map((_, index) => (
                                                <Flex key={`${index}`} align="center" mb="md">
                                                    <ActionIcon
                                                        color="red"
                                                        onClick={() =>
                                                            createCampaignInquiryForm.removeListItem(
                                                                `products.${index}.detailPageLinks`,
                                                                index
                                                            )
                                                        }
                                                    >
                                                        <IconTrash size="1em" />
                                                    </ActionIcon>
                                                    <TextInput
                                                        {...createCampaignInquiryForm.getInputProps(
                                                            `products.${index}.detailPageLinks.${index}.label`
                                                        )}
                                                        ml="md"
                                                        label={t("product_detailpagelink_label")}
                                                        placeholder={t("product_detailpagelink_label_placeholder")}
                                                        style={{ flex: 3 }}
                                                    />
                                                    <TextInput
                                                        {...createCampaignInquiryForm.getInputProps(
                                                            `products.${index}.detailPageLinks.${index}.src`
                                                        )}
                                                        ml="md"
                                                        label={t("product_detailpagelink_src")}
                                                        placeholder={t("product_detailpagelink_src_placeholder")}
                                                        style={{ flex: 5 }}
                                                    />
                                                </Flex>
                                            ))}
                                            <Button
                                                variant="subtle"
                                                size="sm"
                                                leftIcon={<IconPlus size="1em" />}
                                                onClick={() =>
                                                    createCampaignInquiryForm.insertListItem(
                                                        `products.${index}.detailPageLinks`,
                                                        {
                                                            label: "",
                                                            src: "",
                                                        }
                                                    )
                                                }
                                            >
                                                {t("product_detailpagelink_add")}
                                            </Button>
                                        </Stack>
                                    </FormItem>
                                </Grid.Col>
                            </>
                        )}
                    </Grid>
                    <Box m="md">
                        {typeof onRemove === "function" && (
                            <Tooltip label={t("product_remove")}>
                                <ActionIcon color="red" onClick={onRemove}>
                                    <IconTrash />
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </Box>
                </Flex>
            </Card.Section>
            <Card.Section py="md" inheritPadding withBorder>
                <FormItem label={t("campaign_productreviewdestinations")}>
                    <ReviewDestinationsEditor
                        value={destinations}
                        reviewDestinations={reviewDestinations}
                        noReviewDestinationsLabel={noReviewDestinationsLabel}
                        enableCustomProductReviewDestinations={enableCustomProductReviewDestinations}
                        onChange={(destinations) =>
                            createCampaignInquiryForm.setFieldValue(`products.${index}.destinations`, destinations)
                        }
                    />
                </FormItem>
            </Card.Section>
        </Card>
    );
};
